import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useCollection } from '../../hooks/useFirestore'
import { doc, deleteDoc, writeBatch } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import { getFileTypeInfo } from '../../lib/fileIcons'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import './AdminEditor.css'

function SortableHandoutItem({ handout, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: handout.id })

  const isHidden = handout.hidden === true

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : (isHidden ? 0.5 : 1),
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`admin-list-item sortable-item ${isHidden ? 'hidden-item' : ''}`}
    >
      <div className="drag-handle" {...attributes} {...listeners}>
        <span>⋮⋮</span>
      </div>

      <Link to={`/admin/handouts/${handout.id}`} className="sortable-item-content">
        {handout.thumbnailUrl ? (
          <div className="item-image">
            <img src={handout.thumbnailUrl} alt={handout.title} />
          </div>
        ) : (
          <div className="item-image file-type-icon" style={{ background: getFileTypeInfo(handout.fileUrl).color + '22' }}>
            <span>{getFileTypeInfo(handout.fileUrl).icon}</span>
          </div>
        )}
        <div className="item-info">
          <h3>
            {handout.title}
            {isHidden && <span className="hidden-tag">Hidden</span>}
          </h3>
          <p className="text-secondary">
            {handout.description || 'No description'}
          </p>
          {handout.receivedDate && (
            <p className="item-date">{formatHarptosDate(handout.receivedDate)}</p>
          )}
        </div>
      </Link>

      <button
        onClick={(e) => {
          e.preventDefault()
          onDelete(handout.id, handout.title)
        }}
        className="btn btn-small btn-danger"
      >
        Delete
      </button>
    </div>
  )
}

function formatHarptosDate(date) {
  if (!date) return null
  return `${date.day || 1} ${date.month}, ${date.year} DR`
}

export default function HandoutList() {
  const { data: handouts, loading } = useCollection('handouts', { orderByField: 'sortOrder' })
  const [saving, setSaving] = useState(false)
  const [yearFilter, setYearFilter] = useState(null)

  // Get available years from handouts
  const availableYears = [...new Set(
    handouts
      .filter(h => h.receivedDate?.year)
      .map(h => h.receivedDate.year)
  )].sort((a, b) => a - b)

  // Filter handouts by year
  const filteredHandouts = yearFilter
    ? handouts.filter(h => h.receivedDate?.year === yearFilter)
    : handouts

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  async function handleDelete(id, title) {
    if (window.confirm(`Delete handout "${title}"? This cannot be undone.`)) {
      try {
        await deleteDoc(doc(db, 'handouts', id))
      } catch (err) {
        console.error('Error deleting handout:', err)
        alert('Failed to delete handout')
      }
    }
  }

  async function handleDragEnd(event) {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = handouts.findIndex(h => h.id === active.id)
      const newIndex = handouts.findIndex(h => h.id === over.id)

      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = arrayMove(handouts, oldIndex, newIndex)

        // Update sort orders in Firestore
        setSaving(true)
        try {
          const batch = writeBatch(db)
          reordered.forEach((handout, index) => {
            const handoutRef = doc(db, 'handouts', handout.id)
            batch.update(handoutRef, { sortOrder: index })
          })
          await batch.commit()
        } catch (err) {
          console.error('Error updating order:', err)
          alert('Failed to update order')
        } finally {
          setSaving(false)
        }
      }
    }
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>Handouts {saving && <span className="saving-indicator">(Saving...)</span>}</h1>
        <Link to="/admin/handouts/new" className="btn btn-primary">
          + New Handout
        </Link>
      </div>

      {handouts.length === 0 ? (
        <div className="empty-state">
          <p>No handouts yet.</p>
          <p className="text-secondary">Upload documents and materials for your players.</p>
        </div>
      ) : (
        <>
          {availableYears.length > 0 && (
            <div className="admin-filters">
              <div className="filter-group">
                <span className="filter-label">Year</span>
                <select
                  value={yearFilter || ''}
                  onChange={(e) => setYearFilter(e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">All Years</option>
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year} DR</option>
                  ))}
                </select>
              </div>
              <span className="filter-count">
                {filteredHandouts.length} of {handouts.length} handouts
              </span>
            </div>
          )}

          <p className="text-secondary mb-lg">Drag to reorder handouts.</p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={filteredHandouts.map(h => h.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="admin-list">
                {filteredHandouts.map(handout => (
                  <SortableHandoutItem
                    key={handout.id}
                    handout={handout}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </>
      )}
    </div>
  )
}
